export interface RoadtripNationVideoModel {
    jwAssetId: string;
    fullName: string;
    quote: string;
    company: string;
    interviewLocation: string;
    jobTitle: string;
    mp4Url: string;
    thumbUrl: string;
    /** WebVTT captions track. Absent on older assets so players must tolerate undefined. */
    captionsUrl?: string;
    interviewId: number;
    partNumber?: number;
    partTotal?: number;
    /** Set only when the story catalog holds this video, so absent means nothing to link to. */
    storyId?: string;
}

export interface OtherCareerVideoModel {
    id: string;
    videoUrl: string;
    videoTitle?: string;
    storyId?: string;
}

export interface CloudflareVideoModel {
    id: string;
    occupationId?: string;
    videoUrl: string;
    externalVideoUid: string;
    storyId?: string;
}

export interface VideosResponse {
    roadtripNation: RoadtripNationVideoModel[];
    careerOneStop: OtherCareerVideoModel[];
    futureNetwork: OtherCareerVideoModel[];
    cloudflare: CloudflareVideoModel[];
}

export interface QueryVideosParams {
    occupationIds: string[];
}

/**
 * Mirrors the API's `VideoTypeEnum`. `ROADTRIP_NATION` exists only as a story source. It has no
 * `occupation_video` rows so it never appears in a `VideosResponse`.
 */
export enum StorySourceEnum {
    CAREER_ONE_STOP = 0,
    FUTURE_NETWORK = 1,
    CLOUDFLARE_VIDEO = 2,
    ROADTRIP_NATION = 3,
}

/** One story as the browse grid and the standalone page see it, flattened across all four sources. */
export interface StoryVideoModel {
    id: string;
    source: StorySourceEnum;
    /** The creator's display name, e.g. "Roadtrip Nation". Supplied by the API, not mapped here. */
    sourceLabel: string;
    title: string;
    personName?: string;
    jobTitle?: string;
    company?: string;
    videoUrl: string;
    /** Only Roadtrip Nation supplies one. The rest are derived from the video URL. */
    thumbUrl?: string;
    captionsUrl?: string;
    externalVideoUid?: string;
    occupationId?: string;
    durationSeconds?: number;
    /**
     * 1-based position within a multi-part interview, absent when the interview is one video. Many
     * Roadtrip Nation interviews run to two or three parts sharing a person and job title, so
     * without this the grid shows what look like duplicates.
     */
    partNumber?: number;
    partTotal?: number;
}

export interface StorySourceOption {
    source: StorySourceEnum;
    label: string;
}

/**
 * How a search orders its results. `title` pages deterministically. `random` reshuffles per call
 * so it only makes sense for a caller that takes one page and never turns it.
 */
export type StoryVideoOrder = 'title' | 'random';

export interface StoryVideoSearchParams {
    page: number;
    limit: number;
    search?: string;
    sources?: StorySourceEnum[];
    orderBy?: StoryVideoOrder;
}

export interface StoryVideoSearchResponse {
    items: StoryVideoModel[];
    /** Total matching the filters, not the page. */
    count: number;
    /** Every source with stories. Drives the creator filter and is never narrowed by it. */
    sources: StorySourceOption[];
}
